import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import FormControlLabel from '@mui/material/FormControlLabel'
import Typography from '@mui/material/Typography'
import { getFieldTestId } from '../field-helpers'
import type { FieldRenderer } from '../types'
import type { CheckboxProps } from '@mui/material/Checkbox'

const getCheckboxInputTestProps = (testId: string) =>
    ({
        'data-testid': testId
    }) as NonNullable<CheckboxProps['slotProps']>['input']

const linkSx = { textDecoration: 'underline', cursor: 'pointer', color: 'primary.main' }

const CheckboxLabel = ({ text, linkText, onLinkClick, testId }: { text: string; linkText?: string; onLinkClick: () => void; testId: string }) => {
    if (!linkText) {
        return (
            <Typography variant="body2" sx={{ fontSize: 14 }} data-testid={testId}>
                {text}
            </Typography>
        )
    }
    const idx = text.indexOf(linkText)
    const before = idx === -1 ? text : text.slice(0, idx)
    const after = idx === -1 ? '' : text.slice(idx + linkText.length)
    const separator = idx === -1 ? ' ' : ''
    return (
        <Typography variant="body2" sx={{ fontSize: 14 }} data-testid={testId}>
            {before}
            {separator}
            <Box
                component="span"
                sx={linkSx}
                onClick={(e: React.MouseEvent) => {
                    e.preventDefault()
                    onLinkClick()
                }}
                data-testid={`${testId}-link`}>
                {linkText}
            </Box>
            {after}
        </Typography>
    )
}

const renderCheckboxField: FieldRenderer = ({ field, ctx, t }) => {
    const key = field.key
    const label = t(field.label)
    const linkText = field.linkText ? t(field.linkText) : undefined
    return (
        <FormControlLabel
            key={key}
            sx={{ alignItems: 'center' }}
            control={
                <Checkbox
                    checked={Boolean(ctx.form[key])}
                    onChange={e => ctx.setForm((prev: Record<string, unknown>) => ({ ...prev, [key]: e.target.checked }))}
                    disabled={ctx.formDisabled}
                    disableRipple
                    sx={{ '&:hover': { backgroundColor: 'transparent' } }}
                    slotProps={{
                        input: getCheckboxInputTestProps(getFieldTestId(ctx, key, 'input'))
                    }}
                    data-testid={getFieldTestId(ctx, key, 'checkbox')}
                />
            }
            label={
                <CheckboxLabel
                    text={label}
                    linkText={linkText}
                    onLinkClick={() => ctx.onLinkClick?.(key)}
                    testId={getFieldTestId(ctx, key, 'checkbox-label')}
                />
            }
            data-testid={getFieldTestId(ctx, key, 'checkbox-control')}
        />
    )
}

renderCheckboxField.areCtxEqual = (a, b) => a.onLinkClick === b.onLinkClick

export default renderCheckboxField
